import React, { useState, createContext, useMemo, useEffect } from 'react';

import { useMedia } from 'react-use';

import { useParams } from 'react-router-dom';

import { breakpointTo, bpMedium } from 'tokens';

import {
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';

import { currencyFormater } from '@/utils/formatCurrency';

import {
  IProps,
  IStep,
  IUserInfo,
  IUserPayload,
  IUserAddress,
  ICepResponse,
  ILinkInstallment,
  CheckoutProviderProps,
} from '../types/paymentLinkContextTypes';

import {
  initialState,
  initialUserState,
  stepsConfig,
  getStepDeviceLength,
} from '@/constants/configData';

import useGetPaymentLink from '@/services/useGetPaymentLink.service';
import usePostPaymentLink from '@/services/usePostPaymentLink.service';
import useAddress from '@/services/useAddress.service';
import useFormatPayload from '../hooks/useFormatPayload';
import usePaymentSimulate from '@/services/usePaymentSimulate.service';

const PaymentLinkContext = createContext<CheckoutProviderProps>(initialState);

// Global flag to ensure scripts are loaded only once across all instances
const scriptsLoaded = {
  googlePay: false,
  applePay: false,
};

const PaymentLinkContextProvider: React.FC<IProps> = ({ children }) => {
  const isMobile = useMedia(breakpointTo(bpMedium));

  const { id } = useParams();

  const { getLinkIsLoading, getLinkData, getLinkError } = useGetPaymentLink();

  const { postIsLoading, postErrorData, setPostErrorData, handlePostSubmit, handlePostSubmitWallet } =
    usePostPaymentLink();

  const { getSimulationData } = usePaymentSimulate();

  const { getFormatedPayload } = useFormatPayload();

  const [currentStep, setCurrentStep] = useState<IStep>(
    isMobile ? 'details' : 'personal',
  );

  const [userData, setUserData] = useState<IUserInfo>(initialUserState);

  const [disabledFields, setDisabledFields] = useState<string[]>([]);

  const [showAddressFields, setShowAddressFields] = useState<boolean>(false);

  const [loadingCEP, setLoadingCEP] = useState<boolean>(false);

  const [loadingPage, setLoadingPage] = useState<boolean>(true);
  const [digitalWalletEnabled, setDigitalWalletEnabled] =
    useState<boolean>(true);

  const maxSteps = useMemo(
    () =>
      isMobile || digitalWalletEnabled
        ? getStepDeviceLength('mobile')
        : getStepDeviceLength('web'),
    [isMobile, digitalWalletEnabled],
  );

  const getCurrentStepNumberByDevice = () => {
    if (isMobile) return stepsConfig['mobile'];
    return stepsConfig['web'];
  };

  const getCurrentStepNumber = () => {
    const steps = getCurrentStepNumberByDevice();

    const key = currentStep as keyof typeof steps;

    return steps[key] || 1;
  };

  const installmentsOptions = useMemo(() => {
    const options = [];

    const labelWithIssuerRate = (installment: ILinkInstallment) =>
      `${installment.number}x de ${currencyFormater(
        installment.amount,
      )} = ${currencyFormater(installment.total_amount)}`;

    const labelWithoutIssuerRate = (installment: ILinkInstallment) =>
      `${installment.number}x de ${currencyFormater(
        installment.amount,
      )} sem juros`;

    if (getSimulationData) {
      for (const installment of getSimulationData.installments) {
        options.push({
          label: getSimulationData.issuer_rate_forwarding
            ? labelWithIssuerRate(installment)
            : labelWithoutIssuerRate(installment),
          value: installment.number,
        });
      }
    }

    return options;
  }, [getSimulationData]);

  const getByCep = async (
    cep: string,
    setField: UseFormSetValue<IUserAddress>,
    setError: UseFormSetError<IUserAddress>,
    clearErrors: UseFormClearErrors<IUserAddress>,
  ) => {
    setDisabledFields([]);
    clearErrors('cep');
    setShowAddressFields(false);
    if (cep.length === 9) {
      setLoadingCEP(true);
      useAddress(cep)
        .then(response => {
          if (response.data.erro !== 'true') {
            const { bairro, localidade, logradouro, uf } = response.data;

            setField('district', bairro);
            setField('city', localidade);
            setField('street', logradouro);
            setField('state', uf);
            clearErrors();
            disableFieldsByCEP(response.data);
            setLoadingCEP(false);
            setShowAddressFields(true);
          } else {
            setError('cep', { type: 'custom', message: 'CEP inválido' });
            setLoadingCEP(false);
          }
        })
        .catch(() => {
          setLoadingCEP(false);
          setDisabledFields([]);
          setShowAddressFields(true);
        });
    }
  };

  const disableFieldsByCEP = (cepData: ICepResponse) => {
    Object.keys(cepData).forEach((field: string, index: number) => {
      if (Object.values(cepData)[index] !== '') {
        setDisabledFields((disabled: string[]) => [...disabled, field]);
      }
    });
  };

  const handleClearCardFields = () => {
    const data = {
      ...userData,
      card_number: '',
      card_valid_date: '',
      card_client_name: '',
      card_cvv: '',
      installments: '',
    };

    setCurrentStep('credit');
    setUserData(data);
  };

  const submitCheckout = async (userData: IUserInfo) => {
    const payload: IUserPayload = getFormatedPayload(userData);

    return handlePostSubmit(payload);
  };

  const submitCheckoutWallet = async (userData: any, type: string, installments: number) => {

    return handlePostSubmitWallet(userData, type, installments);
  };

  const errorRedirect: string = useMemo(() => {
    const errors = {
      processing: `/processing/${id}`,
      error: `/error/${id}`,
      unavailable: '/unavailable',
      success: `/success/${id}`,
    };

    if (getLinkError) return errors['unavailable'];

    if (!getLinkIsLoading) {
      if (getLinkData?.last_payment_link_intent?.status === 'processing')
        return errors['processing'];
      if (getLinkData?.status === 'paid') return errors['success'];
      if (getLinkData?.status !== 'created') return errors['unavailable'];
    }

    if (postErrorData) {
      if (postErrorData.error_code === 102) return errors['processing'];

      return `${errors['error']}?error_code=${postErrorData.error_code ?? ''}`;
    }

    return '';
  }, [getLinkError, getLinkData, postErrorData]);

  const checkDigitalWallet = async (): Promise<boolean> => {
    await new Promise(resolve => setTimeout(resolve, 1000));


    if (getLinkData?.mof_show_google_wallet || getLinkData?.mof_show_apple_wallet) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    const scriptsConfig = [
      {
        src: 'https://pay.google.com/gp/p/js/pay.js',
        flag: 'googlePay',
      },
      {
        src: 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js',
        flag: 'applePay',
        crossOrigin: 'anonymous',
      },
    ];

    scriptsConfig.forEach(({ src, flag, crossOrigin }) => {
      const flagKey = flag as keyof typeof scriptsLoaded;

      // Only load if not already loaded globally
      if (!scriptsLoaded[flagKey]) {
        const existingScript = document.querySelector(`script[src="${src}"]`);

        if (!existingScript) {
          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.crossOrigin = crossOrigin || null;
          document.head.appendChild(script);
        }

        // Mark as loaded globally
        scriptsLoaded[flagKey] = true;
      }
    });

  }, []);

  useEffect(() => {
    if (!getLinkData) return;

    checkDigitalWallet().then(response => {
      setDigitalWalletEnabled(response);
      setLoadingPage(false);

      if (response) {
        setCurrentStep('paymentMethod');
      }
    });
  }, [getLinkData]);

  return (
    <PaymentLinkContext.Provider
      value={{
        currentStep,
        disabledFields,
        errorRedirect,
        getByCep,
        getCurrentStepNumber,
        getLinkIsLoading,
        getSimulationData,
        handleClearCardFields,
        installmentsOptions,
        isMobile,
        loadingCEP,
        maxSteps,
        paymentLinkData: getLinkData,
        postIsLoading,
        setCurrentStep,
        setPostErrorData,
        setUserData,
        showAddressFields,
        submitCheckout,
        submitCheckoutWallet,
        userData,
        loadingPage,
        digitalWalletEnabled,
      }}
    >
      {children}
    </PaymentLinkContext.Provider>
  );
};

export function withPaymentLinkContext<P>(Component: React.ComponentType<P>) {
  return function WrapperComponent(props: P & { children?: React.ReactNode }) {
    return (
      <PaymentLinkContextProvider>
        <Component {...props} />
      </PaymentLinkContextProvider>
    );
  };
}

export { PaymentLinkContextProvider };

export default PaymentLinkContext;
